import {Controller} from 'stimulus';
import Cookies from 'js-cookie';
import {gtagTrack, initSlider, messages, updateCartCount} from "../../public/common";

export default class extends Controller {
  static targets = ['ageCheckbox', 'denom', 'addButton', 'dialog'];

  loading = false;

  initialize() {
    if (this.hasAgeCheckboxTarget) {
      this.initAgeVerificationFlow();
      this.confirmAge({target: this.ageCheckboxTarget});
    }

    if (this.hasDenomTarget) {
      this.changeDenom({target: this.denomTargets[0]});
    }
  }

  getXsrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]');
    return token && token.getAttribute('content') || '';
  }

  initAgeVerificationFlow() {
    const target = this.ageCheckboxTarget;

    // preselect checkbox if already defined
    const verifiedAge = Cookies.get('age_verified') || 0;
    const requiredAge = target.dataset.age;
    if (verifiedAge >= requiredAge) {
      target.checked = true;
    }
  }

  changeDenom(event) {
    const {target} = event;
    const {id, available} = target?.dataset || {};

    const denoms = Object.values(JSON.parse(Cookies.get('cart') || '{}')).flat();
    const inCartAlready = denoms.includes(id);

    let canBeAdded = true;

    if (this.hasAgeCheckboxTarget) {
      canBeAdded = this.ageCheckboxTarget.checked;
    }

    this.addButtonTargets.forEach(element => {
      element.disabled = canBeAdded ? (inCartAlready || available === 'false') : true;
      element.classList.toggle('cart-add_added', inCartAlready);
    });
  }

  confirmAge(event) {
    const {target, isTrusted, type} = event;
    const {checked} = target;
    const {age, product_id} = target.dataset;
    const cartItems = JSON.parse(Cookies.get('cart') || '{}');
    const previouslyStored = Cookies.get('age_verified') || 0;

    if (checked) {
      Cookies.set('age_verified', Math.max(previouslyStored, age));
    } else if (isTrusted && type === 'change') {
      Cookies.remove('age_verified');
    }

    let canBeAdded;
    if (this.hasDenomTarget) {
      const values = Object.values(cartItems).flat();
      const selectedDenom = this.denomTargets.find(item => !!item.checked)
      canBeAdded = selectedDenom && (selectedDenom.dataset.available !== 'false') && !values.includes(selectedDenom.value);
    } else {
      canBeAdded = !Object.keys(cartItems).includes(product_id)
    }

    this.addButtonTargets.forEach(element => {
      element.disabled = canBeAdded ? !checked : true;
    });
  }

  disableAddButtons($btn) {
    const cartDenoms = Object.values(JSON.parse(Cookies.get('cart') || '{}')).flat();

    const cartAddButtons = $btn.closest('.item-detailed').querySelectorAll('.cart-add');
    [...cartAddButtons].forEach(button => {
      if (button.dataset.productTarget === 'btnDialog') {
        const denomIds = button.dataset.denomIds.split(',');
        const isAllAdded = denomIds.every(id => cartDenoms.includes(id));

        if (isAllAdded) {
          button.setAttribute('disabled', 'disabled');
          button.classList.add('cart-add_added');
        }
      } else {
        button.setAttribute('disabled', 'disabled');
        button.classList.add('cart-add_added');
      }
    });
  }

  addToCart(e) {
    e.preventDefault();
    e.stopPropagation();

    const $btn = e.currentTarget;

    if (!$btn.classList.contains('disabled')) {
      const { href, redirect, denom_id, gtag } = $btn.dataset;
      const denom = this.element.querySelector('input[name="denom_id"]:checked');
      const gtagData = denom?.dataset?.gtag || gtag;
      const denomId = denom?.value || denom_id;

      const data = new URLSearchParams({ denom_id: denomId });

      fetch(href, {
        method: 'PATCH',
        headers: {
          'X-CSRF-TOKEN': this.getXsrfToken(),
        },
        body: data
      })
        .then(() => {
          const changeSteamAccount = document.querySelector('.change-steam-account');
          changeSteamAccount && changeSteamAccount.setAttribute('data-confirm', changeSteamAccount.dataset.message);

          this.disableAddButtons($btn);

          // send add_to_cart event to GA
          gtagTrack($btn.getAttribute('name') || 'add_to_cart', gtagData);

          if (redirect) {
            window.location.href = redirect;
          } else {
            updateCartCount(1);
          }
          this.closeDialogs();
        })
        .catch(e => {
          alert(messages ? messages.error_common : 'Something went wrong. Please try again');
          window.logError(e, {location: 'add product'});
        });
    }
  }

  toggleCartProduct(e) {
    e.preventDefault();
    e.stopPropagation();

    const $btn = e.currentTarget;
    const cardAddedClass = 'toggle-card_added';
    const inTheCart = $btn.classList.contains(cardAddedClass);
    const {url, denomId, gtag} = $btn.dataset;
    const headers = {
      'X-CSRF-TOKEN': this.getXsrfToken(),
    }

    const onError = (error) => {
      alert(messages ? messages.error_common : 'Something went wrong. Please try again');
      window.logError(error, {location: 'product list: add/remove product'});
    }

    const finallyCallback = () => {
      $btn.blur();
    }

    if (inTheCart) {
      // remove product from cart
      fetch(url, {
        method: 'DELETE',
        headers,
      }).then(() => {
        $btn.classList.remove(cardAddedClass);
        updateCartCount(-1);
      })
        .catch(onError)
        .finally(finallyCallback);
    } else {
      // add product to cart
      const body = new URLSearchParams({denom_id: denomId});

      fetch(url, {
        method: 'PATCH',
        headers,
        body,
      }).then(() => {
        $btn.classList.add(cardAddedClass);

        gtagTrack('add_to_cart', gtag);
        updateCartCount(1);
      })
        .catch(onError)
        .finally(finallyCallback);
    }
  }

  removeCartProduct(e) {
    e.stopPropagation();
    e.preventDefault();
    const $btn = e.currentTarget;
    const { url, index, type } = $btn.dataset;

    const headers = {
      'X-CSRF-TOKEN': this.getXsrfToken(),
    }

    fetch(url, {
      method: 'DELETE',
      headers,
    }).then(() => {
      if (type !== 'Products::PinOnReceipt' || (document.querySelectorAll('.media').length === 1)) {
        location.reload();
      } else {
        $btn.closest('.media').remove();
        $(`.cart-overlay-item[data-index=${index}]`).remove();
        try {
          const fullUrl = new URL(`https://${location.host}/${url}`)
          const stockedId = new URLSearchParams(fullUrl.search).get('stocked_product_id');
          document.querySelector(`.product-terms[data-id=${stockedId}]`).remove();
        } catch(e) {}
        updateCartCount(-1);
        initSlider('#cartCarousel');
      }
    }).catch((error) => {
      alert(messages ? messages.error_common : 'Something went wrong. Please try again');
      window.logError(error, {location: 'delete product'});
    });
  }

  openDialog(e) {
    const $dialog = this.dialogTarget;

    if (this.hasAgeCheckboxTarget) {
      this.initAgeVerificationFlow();
      this.confirmAge({target: this.ageCheckboxTarget});
    }

    const ageChecked = this.hasAgeCheckboxTarget ? this.ageCheckboxTarget.checked : true;

    const radioFirst = $dialog.querySelector('input[name="denom_id"]:first-of-type');
    const $buttons = $dialog.querySelectorAll('.cart-add');

    $buttons.forEach(button => {
      button.disabled = radioFirst ? !(radioFirst && ageChecked) : !ageChecked;
    });

    if (radioFirst) {
      radioFirst.checked = true;
      this.changeDenom({target: radioFirst});
    }

    if (ageChecked && !$dialog.querySelector('input[type="radio"]:not(:checked)')) {
      // skip the dialog, just click on the button in the dialog if no checked radio buttons (single price OR open denom)
      $buttons[0].click();
    } else {
      $dialog.showModal();
    }
  }

  closeDialog(e) {
    const $target = e.currentTarget;
    const $dialog = $target.closest('dialog');
    $dialog.close();
  }

  closeDialogs() {
    if (this.hasDialogTarget) {
      this.dialogTargets.forEach(dialog => dialog.close());
    }

    document.activeElement.blur();
  }
}
